import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Play, Trash2, MoveUp, MoveDown, Plus, Globe, Save, Image } from 'lucide-react';
import { useSequenceStore } from '../store/sequenceStore';
import { synthesizeSpeech, preGenerateAudio } from '../services/pollyService';
import type { Sequence, SequenceSettings, SequenceMedia } from '../types';
import GeneratingDots from '../components/GeneratingDots';
import PasswordPrompt from '../components/PasswordPrompt';
import MediaDialog from '../components/MediaDialog';
import { PUBLIC_SEQUENCE_EDIT_PASSWORD } from '../config/constants';
import { LanguageCode, VoiceId } from "@aws-sdk/client-polly";


// Voice options by engine type
const VOICE_OPTIONS = {
  neural: [
    { id: 'Joanna', name: 'Joanna (Female)' },
    { id: 'Matthew', name: 'Matthew (Male)' },
    { id: 'Ruth', name: 'Ruth (Female)' },
    { id: 'Stephen', name: 'Stephen (Male)' },
    { id: 'Kimberly', name: 'Kimberly (Female)'},
    { id: 'Justin', name: 'Justin (Male)'},
    { id: 'Joey', name: 'Joey (Male)'},
  ],
  standard: [
    { id: 'Ivy', name: 'Ivy (Female)' },
    { id: 'Joanna', name: 'Joanna (Female)' },
    { id: 'Joey', name: 'Joey (Male)' },
    { id: 'Justin', name: 'Justin (Male)' },
    { id: 'Kendra', name: 'Kendra (Female)' },
    { id: 'Kimberly', name: 'Kimberly (Female)' },
    { id: 'Matthew', name: 'Matthew (Male)' },
    { id: 'Salli', name: 'Salli (Female)' }
  ]
};

function SequenceDesign() {
  const navigate = useNavigate();
  const { selectedSequence, addSequence, updateSequence, user } = useSequenceStore();
  const [showSettings, setShowSettings] = useState(false);
  const [selectedSentenceIndex, setSelectedSentenceIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [mediaDialogIndex, setMediaDialogIndex] = useState<number | null>(null);
  
  const [sequence, setSequence] = useState<Sequence>(
    selectedSequence || {
      id: crypto.randomUUID(),
      name: '',
      sentences: [],
      media: [],
      isPublic: false,
      settings: {
        voice: 'Joanna',
        rate: 1,
        language: 'en-US',
        pitch: 1,
        engine: 'neural',
        autoplayGifs: true
      },
      audioCache: {}
    }
  );
  
  // Get available voices based on selected engine
  const availableVoices = VOICE_OPTIONS[sequence.settings.engine || 'neural'];
  
  // Ensure the selected voice is available for the current engine
  useEffect(() => {
    if (!availableVoices) return;
    
    const isVoiceAvailable = availableVoices.some(voice => voice.id === sequence.settings.voice);
    if (!isVoiceAvailable && availableVoices.length > 0) {
      handleSettingsChange({ voice: (availableVoices[0].id as VoiceId) });
    }
  }, [sequence.settings.engine, availableVoices]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSequence({ ...sequence, name: e.target.value });
  };

  const handleAddSentence = () => {
    setSequence({
      ...sequence,
      sentences: [...sequence.sentences, '']
    });
    setSelectedSentenceIndex(sequence.sentences.length);
  };

  const handleSentenceChange = (index: number, value: string) => {
    const newSentences = [...sequence.sentences];
    newSentences[index] = value;
    setSequence({ ...sequence, sentences: newSentences });
  };

  const handleDeleteSentence = (index: number) => {
    // Remove any associated media
    const newMedia = sequence.media?.filter(m => m.sentenceIndex !== index) || [];
    
    // Update indexes for media attached to sentences after the deleted one
    newMedia.forEach(m => {
      if (m.sentenceIndex > index) {
        m.sentenceIndex--;
      }
    });
    
    setSequence({
      ...sequence,
      sentences: sequence.sentences.filter((_, i) => i !== index),
      media: newMedia
    });
    setSelectedSentenceIndex(null);
  };

  const handleMoveSentence = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sequence.sentences.length) return;

    const newSentences = [...sequence.sentences];
    [newSentences[index], newSentences[newIndex]] = [newSentences[newIndex], newSentences[index]];
    
    // Update media indexes
    const newMedia = sequence.media?.map(m => {
      if (m.sentenceIndex === index) {
        return { ...m, sentenceIndex: newIndex };
      }
      if (m.sentenceIndex === newIndex) {
        return { ...m, sentenceIndex: index };
      }
      return m;
    }) || [];
    
    setSequence({ ...sequence, sentences: newSentences, media: newMedia });
    setSelectedSentenceIndex(newIndex);
  };

  const handleSettingsChange = (settings: Partial<SequenceSettings>) => {
    setSequence({ 
      ...sequence, 
      settings: { ...sequence.settings, ...settings },
      audioCache: {} // Clear the entire cache when settings change
    });
  };

  const handlePublicToggle = () => {
    if (!sequence.isPublic) {
      setShowPasswordPrompt(true);
    } else {
      setSequence({
        ...sequence,
        isPublic: false
      });
    }
  };

  const handlePasswordSubmit = (password: string) => {
    if (password === PUBLIC_SEQUENCE_EDIT_PASSWORD) {
      setSequence({
        ...sequence,
        isPublic: true
      });
      setShowPasswordPrompt(false);
    } else {
      alert('Incorrect password');
    }
  };

  const handleMediaClick = (index: number) => {
    setMediaDialogIndex(index);
    setShowMediaDialog(true);
  };

  const handleMediaSave = (media: SequenceMedia) => {
    const newMedia = [...(sequence.media || [])];
    const existingIndex = newMedia.findIndex(m => m.sentenceIndex === mediaDialogIndex);
    
    if (existingIndex >= 0) {
      newMedia[existingIndex] = media;
    } else {
      newMedia.push(media);
    }
    
    setSequence({
      ...sequence,
      media: newMedia
    });
    setShowMediaDialog(false);
  };

  const handleMediaDelete = () => {
    if (mediaDialogIndex === null) return;
    
    const newMedia = sequence.media?.filter(m => m.sentenceIndex !== mediaDialogIndex) || [];
    setSequence({
      ...sequence,
      media: newMedia
    });
    setShowMediaDialog(false);
  };

  const handleSave = async () => {
    if (!sequence.name.trim()) {
      alert('Please enter a sequence name');
      return;
    }
    
    setIsSaving(true);
    
    try {
      console.log('Pre-generating audio for sequence before saving...');
      const sequenceToSave = await preGenerateAudio(sequence);
      
      if (selectedSequence) {
        updateSequence(sequenceToSave, true);
      } else {
        addSequence(sequenceToSave);
      }
      
      navigate('/');
    } catch (error) {
      console.error('Error saving sequence:', error);
      alert('Error saving sequence. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePlaySentence = async (index: number) => {
    try {
      await synthesizeSpeech(
        sequence.sentences[index],
        sequence.settings,
        sequence
      );
    } catch (error) {
      console.error('Failed to play sentence:', error);
    }
  };

  const getMediaForSentence = (index: number) => {
    return sequence.media?.find(m => m.sentenceIndex === index);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {selectedSequence ? 'Edit Sequence' : 'New Sequence'}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <Settings size={24} />
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              isSaving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isSaving ? (
              <>
                <GeneratingDots />
              </>
            ) : (
              <>
                <Save size={20} />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Sequence Name
            </label>
            <input
              type="text"
              id="name"
              value={sequence.name}
              onChange={handleNameChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter sequence name"
            />
          </div>
          <div className="flex items-center mt-6">
            <button
              onClick={handlePublicToggle}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md ${
                sequence.isPublic 
                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              title={sequence.isPublic ? "Make private" : "Make public"}
            >
              <Globe size={20} />
              <span>{sequence.isPublic ? 'Public' : 'Private'}</span>
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sequence Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Voice Engine</label>
                <select
                  value={sequence.settings.engine}
                  onChange={(e) => handleSettingsChange({ engine: e.target.value as 'neural' | 'standard' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="neural">Neural (Higher quality, fewer voices)</option>
                  <option value="standard">Standard (More voices)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Voice</label>
                <select
                  value={sequence.settings.voice}
                  onChange={(e) => handleSettingsChange({ voice: (e.target.value as VoiceId) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {availableVoices && availableVoices.map(voice => (
                    <option key={voice.id} value={voice.id}>{voice.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Language</label>
                <select
                  value={sequence.settings.language}
                  onChange={(e) => handleSettingsChange({ language: (e.target.value as LanguageCode) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="en-AU">English (Australian)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rate ({sequence.settings.rate}x)
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={sequence.settings.rate}
                  onChange={(e) => handleSettingsChange({ rate: parseFloat(e.target.value) })}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Pitch ({sequence.settings.pitch})
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={sequence.settings.pitch}
                  onChange={(e) => handleSettingsChange({ pitch: parseFloat(e.target.value) })}
                  className="mt-1 block w-full"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoplayGifs"
                  checked={sequence.settings.autoplayGifs}
                  onChange={(e) => handleSettingsChange({ autoplayGifs: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoplayGifs" className="ml-2 block text-sm text-gray-900">
                  Autoplay GIFs in player
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Sentences</h2>

          <div className="space-y-3">
            {sequence.sentences.map((sentence, index) => {
              const media = getMediaForSentence(index);
              return (
                <div
                  key={index}
                  className={`flex items-start space-x-3 p-3 rounded-lg ${
                    selectedSentenceIndex === index ? 'bg-blue-50 ring-2 ring-blue-500' : 'bg-gray-90'
                  }`}
                  onClick={() => setSelectedSentenceIndex(index)}
                >
                  <div className="flex-1 space-y-3">
                    <textarea
                      value={sentence}
                      onChange={(e) => handleSentenceChange(index, e.target.value)}
                      className="w-full bg-red-50/50 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={2}
                    />
                    {media && (
                      <div className="relative">
                        <img
                          src={media.url}
                          alt=""
                          className="w-full max-h-48 object-contain rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <button
                      onClick={() => handleMoveSentence(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 order-2"
                    >
                      <MoveUp size={20} />
                    </button>
                    <button
                      onClick={() => handleMoveSentence(index, 'down')}
                      disabled={index === sequence.sentences.length - 1}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 order-4"
                    >
                      <MoveDown size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteSentence(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded order-3"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() => handlePlaySentence(index)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded order-1"
                    >
                      <Play size={20} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMediaClick(index);
                      }}
                      className={`p-1 rounded order-5 ${
                        media
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Image size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Add Sentence button below the sentences list */}
          <div className="flex justify-center mt-4">
            <button
              onClick={handleAddSentence}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus size={20} />
              <span>Add Sentence</span>
            </button>
          </div>
        </div>
      </div>

      {/* Password prompt modal */}
      <PasswordPrompt 
        isOpen={showPasswordPrompt}
        onSubmit={handlePasswordSubmit}
        onCancel={() => setShowPasswordPrompt(false)}
      />

      {/* Media dialog */}
      <MediaDialog
        isOpen={showMediaDialog}
        onClose={() => setShowMediaDialog(false)}
        onSave={handleMediaSave}
        onDelete={handleMediaDelete}
        sentenceIndex={mediaDialogIndex || 0}
        existingMedia={mediaDialogIndex !== null ? getMediaForSentence(mediaDialogIndex) : undefined}
        cloudFolder={user?.cloudFolder}
        isPublic={sequence.isPublic}
        sequenceName={sequence.name || 'untitled'}
      />
    </div>
  );
}

export default SequenceDesign;